import apiClient from './api';
import { unwrapResponse } from '@/utils/apiResponse';

export type NotificationAudience = 'all' | 'customers' | 'restaurants' | 'delivery_partners' | 'user';
export type NotificationKind = 'broadcast' | 'ticket';

export const USER_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'customers', label: 'Customer only' },
  { value: 'restaurants', label: 'Restaurant only' },
  { value: 'delivery_partners', label: 'Delivery partner only' },
] as const;

export function audienceLabel(audience?: string, targetRole?: string | null) {
  if (audience === 'customers' || targetRole === 'customer') return 'Customer only';
  if (audience === 'restaurants' || targetRole === 'restaurant_partner') return 'Restaurant only';
  if (audience === 'delivery_partners' || targetRole === 'delivery_partner') return 'Delivery partner only';
  if (audience === 'user') return 'Individual';
  return 'All';
}

export interface AppNotification {
  id: number;
  kind: NotificationKind;
  title: string;
  message: string;
  url: string | null;
  imageUrl: string | null;
  audience: NotificationAudience;
  targetRole: string | null;
  targetAccountId: number | null;
  sourceType: string | null;
  sourceId: number | null;
  createdBy: number | null;
  createdAt: string;
}

export interface SaveNotificationPayload {
  title: string;
  message: string;
  url?: string | null;
  imageUrl?: string | null;
  audience: Exclude<NotificationAudience, 'user'>;
}

export interface ListNotificationsResponse {
  notifications: AppNotification[];
  total: number;
  page: number;
  pageSize: number;
}

export const notificationsApi = {
  list: (params?: { page?: number; pageSize?: number; search?: string; audience?: string }) =>
    apiClient.get('/admin/notifications', { params }).then((res) => unwrapResponse<ListNotificationsResponse>(res)),

  get: (id: number) =>
    apiClient.get(`/admin/notifications/${id}`).then((res) => unwrapResponse<{ notification: AppNotification }>(res)),

  create: (payload: SaveNotificationPayload) =>
    apiClient
      .post('/admin/notifications', payload)
      .then((res) => unwrapResponse<{ notification: AppNotification; message: string }>(res)),

  remove: (id: number) =>
    apiClient.delete(`/admin/notifications/${id}`).then((res) => unwrapResponse<{ message: string }>(res)),

  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient
      .post('/admin/notifications/upload', formData, {
        timeout: 60000,
        transformRequest: [
          (data, headers) => {
            if (data instanceof FormData) {
              delete (headers as { 'Content-Type'?: string })['Content-Type'];
            }
            return data;
          },
        ],
      })
      .then((res) => unwrapResponse<{ message: string; file: { url: string; path: string; fileName: string } }>(res));
  },
};
